/**
 * AssignRoleDialog — Assign a custom role directly to a user (W1a-07).
 *
 * v1 surfaces ONLY custom roles for assignment. System roles
 * (sysadmin/owner/admin/member/viewer) flow through the W1a-04 PATCH
 * /users/:id endpoint where role-change has its own escalation guards
 * + audit story. The list is filtered to `type === 'custom'`.
 *
 * The server enforces:
 *   - Self-assignment is forbidden (separation-of-duties).
 *   - Caller must hold every permission the role grants
 *     (PRIVILEGE_ESCALATION 403 if not).
 *   - System role assignment via this endpoint → 403.
 *   - Already-assigned → 409 ALREADY_ASSIGNED.
 *
 * Reason field is captured locally (CMMC 3.3.2 / SOC 2 CC6.3) but not yet
 * persisted on the server side — the audit row records actor + role + IP +
 * UA. Captured as a follow-up to surface the reason in audit details.
 */

import { useState, useEffect } from 'react';
import {
  Shield, Check, AlertTriangle, Loader2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useRoles,
  useAssignRoleToUser,
  type RoleRow,
} from '@/lib/use-users';
import type { User } from '@/lib/mock-users-data';

export interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onAssigned?: (user: User, addedRoleIds: string[], reason: string) => void;
}

export function AssignRoleDialog({
  open, onOpenChange, user, onAssigned,
}: AssignRoleDialogProps) {
  const { roles: allRoles, isLoading: rolesLoading } = useRoles();
  const assignMutation = useAssignRoleToUser();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedRoleId(null);
      setReason('');
    }
  }, [open]);

  if (!user) return null;

  // Tenant roles only, and not the retired ones: a retired role grants
  // nothing, so assigning it would be a no-op the screen could not explain.
  const customRoles = allRoles.filter(r => r.kind === 'custom' && r.retiredAt === null);
  const canSave = selectedRoleId !== null && reason.trim().length >= 5 && !assignMutation.isPending;

  const handleSubmit = async () => {
    if (!canSave || !selectedRoleId) return;
    try {
      await assignMutation.mutateAsync({
        userId: user.id,
        roleId: selectedRoleId,
      });
      toast.success(
        `Role assigned to ${user.fullName}`,
        { description: 'Audit event recorded.' },
      );
      onAssigned?.(user, [selectedRoleId], reason.trim());
      onOpenChange(false);
    } catch (err) {
      const error = err as Error & { response?: { error?: { code?: string; message?: string } } };
      const code = error.response?.error?.code;
      const message = error.response?.error?.message ?? error.message ?? 'Could not assign role';
      if (code === 'PRIVILEGE_ESCALATION') {
        toast.error(`Cannot assign role: ${message}`);
      } else if (code === 'ALREADY_ASSIGNED') {
        toast.error('User already has this role');
      } else if (code === 'FORBIDDEN') {
        toast.error(message);
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Assign role
          </DialogTitle>
          <DialogDescription>
            Grant {user.fullName} a custom role. To change their system role, use the Edit User dialog instead.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5 space-y-5">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Available custom roles {customRoles.length > 0 && `(${customRoles.length})`}
              </p>
              {rolesLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading roles…
                </div>
              ) : customRoles.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">
                  No custom roles defined. Create one in <span className="font-mono">Users → Roles → New Role</span>.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {customRoles.map(r => (
                    <RoleOption
                      key={r.id}
                      role={r}
                      selected={selectedRoleId === r.id}
                      onSelect={() => setSelectedRoleId(r.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2 pt-2 border-t">
              <Label className="text-xs font-semibold">
                Reason for assignment <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Promoted to Compliance Lead, needs Compliance Officer role"
                rows={3}
                className="text-xs"
                disabled={assignMutation.isPending}
              />
              <p className="text-[10px] text-muted-foreground">
                Captured for the audit trail (CMMC 3.3.2 / SOC 2 CC6.3). Minimum 5 characters.
              </p>
            </div>

            {selectedRoleId !== null && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  The server validates that you currently hold every permission this role grants.
                  Custom-role grants do not yet activate runtime permission checks (F-W1a-07.1) — the
                  assignment is recorded and audited.
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <span className="text-xs text-muted-foreground">
              {selectedRoleId !== null ? '1 selected' : '0 selected'}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={assignMutation.isPending}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!canSave}>
                {assignMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Assigning…
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1.5" /> Assign role
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleOption({
  role, selected, onSelect,
}: {
  role: RoleRow;
  selected: boolean;
  onSelect: () => void;
}) {
  // The count the SERVER computed, from the grants it projects everywhere
  // else. A length taken here is the second answer B1b deleted.
  const permCount = role.permissionCount;
  return (
    <label
      className={cn(
        'flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors',
        selected ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
      )}
    >
      <Checkbox checked={selected} onCheckedChange={onSelect} className="mt-0.5" />
      <Shield className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-medium">{role.name}</span>
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">Custom</Badge>
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 tabular-nums">
            {permCount} perms
          </Badge>
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 tabular-nums">
            {role.assignedUserCount} users
          </Badge>
        </div>
        {role.description && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{role.description}</p>
        )}
      </div>
    </label>
  );
}
